import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Archive, BarChart3, Gauge, History, ListTree, RefreshCcw } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useDataScopeStore } from '@/client/stores/data-scope';

import { OverviewTab } from './VirtualTours/OverviewTab';
import { CapacityTab } from './VirtualTours/CapacityTab';
import { ToursTab } from './VirtualTours/ToursTab';
import { InventoryTab } from './VirtualTours/InventoryTab';
import { ErrorsTab } from './VirtualTours/ErrorsTab';
import { SyncTab } from './VirtualTours/SyncTab';

type TabKey = 'overview' | 'capacity' | 'tours' | 'inventory' | 'errors' | 'sync';

const TABS: { id: TabKey; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'capacity', label: 'Capacity', icon: Gauge },
  { id: 'tours', label: 'Tours', icon: ListTree },
  { id: 'inventory', label: 'Matterport Inventory', icon: Archive },
  { id: 'errors', label: 'Errors & Logs', icon: History },
  { id: 'sync', label: 'Sync', icon: RefreshCcw },
];

const VirtualToursPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Listing Management', href: '#' }, { label: 'Virtual Tours' }]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (TABS.some((t) => t.id === searchParams.get('tab')) ? searchParams.get('tab') : 'overview') as TabKey;
  const setTab = (next: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const scope = useDataScopeStore((s) => s.scope);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-8xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Virtual Tours</h1>
          <p className="mt-1 text-sm text-gray-500">
            Matterport tours linked to listings — sync health, capacity and inventory.
          </p>
        </div>

        <div className="mb-6 flex gap-1 overflow-x-auto border-b border-gray-200">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                tab === id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab scope={scope} />}
        {tab === 'capacity' && <CapacityTab />}
        {tab === 'tours' && <ToursTab scope={scope} />}
        {tab === 'inventory' && <InventoryTab />}
        {tab === 'errors' && <ErrorsTab />}
        {tab === 'sync' && <SyncTab />}
      </div>
    </div>
  );
};

export default VirtualToursPage;
